import type { ButtonHTMLAttributes } from 'react';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { classNames } from '../../utils/class-names';

import styles from './presentation-preview.module.css';

type PresentationPreviewButtonsProps = {
  active?: boolean;
  image?: string;
  label?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * @param {boolean} [active] Whether the slide is active.
 * @default undefined
 * @param {string} [image] The image to display in the slide.
 * @default undefined
 * @param {string} [label] The label to display in the slide.
 * @default undefined
 */
export function SlideButton({ active, image, label, ...props }: PresentationPreviewButtonsProps) {
  return (
    <button
      data-testid="presentation-preview-slide-button"
      type="button"
      role="tab"
      className={classNames(styles.slideButton, active && styles.active)}
      {...props}
    >
      <img src={image} alt={label} />
      <span>{label}</span>
    </button>
  );
}

export function PrevSlideButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <button
      data-testid="presentation-preview-previous-slide-button"
      type="button"
      className={classNames(styles.slideControlButton, styles.prevSlide)}
      {...props}
    >
      <ArrowLeftIcon />
      {t('presentationPreview.button.prevSlide.label')}
    </button>
  );
}

export function NextSlideButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <button
      data-testid="presentation-preview-next-slide-button"
      type="button"
      className={classNames(styles.slideControlButton, styles.nextSlide)}
      {...props}
    >
      {t('presentationPreview.button.nextSlide.label')}
      <ArrowRightIcon />
    </button>
  );
}

export function ScrollLeftButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <button
      data-testid="presentation-preview-scroll-left-button"
      type="button"
      aria-label={t('presentationPreview.button.scrollLeft.label')}
      className={styles.scrollButton}
      {...props}
    >
      <ChevronLeftIcon />
    </button>
  );
}

export function ScrollRightButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();

  return (
    <button
      data-testid="presentation-preview-scroll-right-button"
      type="button"
      aria-label={t('presentationPreview.button.scrollRight.label')}
      className={styles.scrollButton}
      {...props}
    >
      <ChevronRightIcon />
    </button>
  );
}
